import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import { getStorage, setStorage } from '../utilities/storage';
import usersData from '../mock/users'; // Importar el mock de usuarios

const InstructorProfile = ({ onNavigate }) => {
  const { currentUser, updateCurrentUser } = useContext(AuthContext);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    headline: '',
    bio: '',
    website: '',
    twitter: '',
    linkedin: '',
    youtube: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        firstName: currentUser.first_name || '',
        lastName: currentUser.last_name || '',
        headline: currentUser.headline || '',
        bio: currentUser.bio || '',
        website: currentUser.website || '',
        twitter: currentUser.twitter || '',
        linkedin: currentUser.linkedin || '',
        youtube: currentUser.youtube || ''
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setMessage('');

    if (!currentUser) {
      setMessage('Error: No hay usuario autenticado.');
      return;
    }

    // Actualizar el objeto currentUser con los nuevos datos del perfil
    const updatedUser = {
      ...currentUser,
      first_name: profileData.firstName,
      last_name: profileData.lastName,
      headline: profileData.headline,
      bio: profileData.bio,
      website: profileData.website,
      twitter: profileData.twitter,
      linkedin: profileData.linkedin,
      youtube: profileData.youtube
    };

    // Actualizar el AuthContext y localStorage
    updateCurrentUser(updatedUser);

    // Actualizar el mock de usuarios (simulación de persistencia en "base de datos")
    const updatedUsersData = usersData.map(user =>
      user.id === updatedUser.id ? updatedUser : user
    );
    setStorage('users', updatedUsersData); // Guardar el mock actualizado en localStorage

    setMessage('¡Perfil actualizado con éxito!');
    setIsEditing(false);
  };

  if (!currentUser || currentUser.role !== 'instructor') {
    return (
      <div className="container mx-auto px-4 py-8 text-red-500 text-center">
        Acceso denegado. Esta página es solo para instructores.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Perfil del Instructor</h1>

      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
          <span className="block sm:inline">{message}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Información básica</h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Editar
            </button>
          )}
        </div>

        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Nombre</label>
              <input
                type="text"
                name="firstName"
                value={profileData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Apellido</label>
              <input
                type="text"
                name="lastName"
                value={profileData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Titular (Headline)</label>
            <input
              type="text"
              name="headline"
              value={profileData.headline}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
              placeholder="Ej: Desarrollador Full Stack y Mentor"
              disabled={!isEditing}
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Biografía</label>
            <textarea
              name="bio"
              value={profileData.bio}
              onChange={handleChange}
              rows="6"
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
              placeholder="Cuéntanos sobre tu experiencia, habilidades y lo que te apasiona enseñar."
              disabled={!isEditing}
            ></textarea>
          </div>

          <h2 className="text-xl font-bold mb-4">Enlaces de redes sociales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Sitio web</label>
              <input
                type="url"
                name="website"
                value={profileData.website}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="https://tuweb.com"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Twitter</label>
              <input
                type="url"
                name="twitter"
                value={profileData.twitter}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="https://twitter.com/tuusuario"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">LinkedIn</label>
              <input
                type="url"
                name="linkedin"
                value={profileData.linkedin}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="https://linkedin.com/in/tuperfil"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">YouTube</label>
              <input
                type="url"
                name="youtube"
                value={profileData.youtube}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="https://youtube.com/tucanal"
                disabled={!isEditing}
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Guardar Cambios
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default InstructorProfile;